import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import { useMemo } from 'react';
import { MilestonePhaseItemDto } from 'src/api/FiretrolApiClient';
import {
  MilestoneItemScope,
  useMoveMilestoneItem,
} from 'src/api/queries/milestones/useMilestoneItemMutations';
import { IMilestoneMoveActionIconsProps } from 'src/app/milestones/MilestoneMoveActionIcons';
import {
  getMilestoneItemMoveBounds,
  isMilestoneItemDefaultSorting,
  isMilestoneItemFilterActive,
} from 'src/app/milestones/milestoneItemOrder';

export const MILESTONE_MOVE_SORT_BLOCKED_REASON =
  'Sorted by another column. Reset the table below to reorder milestones.';

export const MILESTONE_MOVE_FILTER_BLOCKED_REASON =
  'Rows are filtered. Clear the filters to reorder milestones.';

export interface IUseMilestoneItemMoveProps {
  /** Every item of the job or template, not just the visible rows. */
  items: MilestonePhaseItemDto[];
  scope: MilestoneItemScope;
  sorting: MRT_SortingState;
  columnFilters: MRT_ColumnFiltersState;
  globalFilter: string;
}

/** @registry Wires MilestoneMoveActionIcons for a milestone item table: group first/last from the full item list, the move mutation, and the block while a non-default sort or any filter is active. */
export const useMilestoneItemMove = ({
  items,
  scope,
  sorting,
  columnFilters,
  globalFilter,
}: IUseMilestoneItemMoveProps) => {
  const { mutate, isPending } = useMoveMilestoneItem();
  const bounds = useMemo(() => getMilestoneItemMoveBounds(items), [items]);
  const blockedReason = !isMilestoneItemDefaultSorting(sorting)
    ? MILESTONE_MOVE_SORT_BLOCKED_REASON
    : isMilestoneItemFilterActive(columnFilters, globalFilter)
    ? MILESTONE_MOVE_FILTER_BLOCKED_REASON
    : undefined;

  return (item: MilestonePhaseItemDto): IMilestoneMoveActionIconsProps => {
    const itemBounds = bounds.get(item.id);
    return {
      isFirst: itemBounds?.isFirst ?? true,
      isLast: itemBounds?.isLast ?? true,
      blockedReason,
      pending: isPending,
      onMove: (direction) => mutate({ scope, itemId: item.id, direction }),
    };
  };
};
